import { MobileManager } from '../core/MobileManager';

/**
 * TouchPauseButton - Touch-friendly pause button for mobile
 * Positioned in top-right corner, only visible on touch devices
 */
export class TouchPauseButton {
  private container: HTMLElement;
  private element: HTMLElement;
  private onPause: () => void;

  constructor(container: HTMLElement, onPause: () => void) {
    this.container = container;
    this.onPause = onPause;

    this.element = this.createButton();
    this.container.appendChild(this.element);

    this.updateVisibility();
    window.addEventListener('resize', this.updateVisibility);
  }

  private createButton(): HTMLElement {
    const scale = MobileManager.getScale();
    const size = Math.round(44 * scale);
    const top = Math.round(75 * scale); // Below score display
    const right = Math.round(30 * scale);

    const button = document.createElement('button');
    button.className = 'touch-pause-button';
    button.innerHTML = `
      <span class="pause-icon">❚❚</span>
    `;

    button.style.cssText = `
      position: fixed;
      top: ${top}px;
      right: ${right}px;
      width: ${size}px;
      height: ${size}px;
      border-radius: 8px;
      background: rgba(0, 0, 0, 0.5);
      border: 2px solid rgba(0, 255, 255, 0.6);
      color: #00ffff;
      font-size: ${Math.round(18 * scale)}px;
      cursor: pointer;
      z-index: 1000;
      display: none;
      align-items: center;
      justify-content: center;
      touch-action: manipulation;
      -webkit-tap-highlight-color: transparent;
      box-shadow: 0 0 10px rgba(0, 255, 255, 0.3);
    `;

    // Add styles for the icon
    const style = document.createElement('style');
    style.id = 'touch-pause-button-styles';

    if (!document.getElementById('touch-pause-button-styles')) {
      style.textContent = `
        .touch-pause-button {
          pointer-events: auto;
        }

        .touch-pause-button.visible {
          display: flex !important;
        }

        .touch-pause-button:active {
          background: rgba(0, 255, 255, 0.3);
          transform: scale(0.95);
        }

        .touch-pause-button .pause-icon {
          letter-spacing: 2px;
          text-shadow: 0 0 8px #00ffff;
        }
      `;
      document.head.appendChild(style);
    }

    button.addEventListener('touchstart', this.handleTouch, { passive: false });
    button.addEventListener('click', this.handleClick);

    return button;
  }

  private handleTouch = (e: TouchEvent): void => {
    e.preventDefault();
    e.stopPropagation();
    this.onPause();
  };

  private handleClick = (e: MouseEvent): void => {
    e.preventDefault();
    e.stopPropagation();
    this.onPause();
  };

  private updateVisibility = (): void => {
    if (MobileManager.isTouchDevice()) {
      this.element.classList.add('visible');
    } else {
      this.element.classList.remove('visible');
    }
  };

  /**
   * Show/hide the pause button
   */
  public setVisible(visible: boolean): void {
    if (visible && MobileManager.isTouchDevice()) {
      this.element.classList.add('visible');
    } else {
      this.element.classList.remove('visible');
    }
  }

  /**
   * Cleanup and remove the button
   */
  public destroy(): void {
    this.element.removeEventListener('touchstart', this.handleTouch);
    this.element.removeEventListener('click', this.handleClick);
    window.removeEventListener('resize', this.updateVisibility);
    this.element.remove();
  }
}
